import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  Input,
  TemplateRef,
  ViewChild,
  inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import * as QRCode from 'qrcode';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { CommonService } from 'src/app/services/common.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

type WebchatLoginMode = 'anonymous' | 'mobile';

@Component({
  selector: 'personal-inbound-reason',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzSpinModule,
    NzPaginationModule,
    NzFormModule,
    NzSelectModule,
    NzInputModule,
    NzButtonModule,
    NzDrawerModule,
    NzEmptyModule,
    NzRadioModule,
    NzModalModule,
    NzIconModule,
    NzToolTipModule,
    NzSwitchModule,
    NzAvatarModule,
    NzBadgeModule,
    NzTagModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './personal-inbound-reason.component.html',
  styleUrls: ['./personal-inbound-reason.component.less']
})
export class PersonalInboundReasonComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() serviceNumberIdInput = '';
  @Input() tenantIdInput = '';
  @Input() embedded = false;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly commonService = inject(CommonService);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly ngZone = inject(NgZone);

  @ViewChild('deleteConfirmTpl', { static: true }) deleteConfirmTpl!: TemplateRef<{}>;
  @ViewChild('deleteConfirmTitleTpl', { static: true }) deleteConfirmTitleTpl!: TemplateRef<{}>;
  @ViewChild('drawerTitle', { static: true }) drawerTitle!: TemplateRef<{}>;
  @ViewChild('pageScroll') pageScroll?: ElementRef<HTMLElement>;
  @ViewChild('mobileLoadMoreAnchor') mobileLoadMoreAnchor?: ElementRef<HTMLDivElement>;
  @ViewChild('searchInputElement') searchInputElement?: ElementRef<HTMLInputElement>;

  tenantId = '';
  serviceNumberId = '';
  loading = false;
  listOfData: any[] = [];
  pageIndex = 1;
  pageSize = 10;
  total = 0;
  searchKeywords = '';
  typeFilter: string | null = null;
  statusFilter: string | null = null;
  direction = 'desc';
  refreshTime = Date.now();
  isMobileView = false;
  isLoadingMore = false;
  hasMoreMobile = false;
  mobileSearchExpanded = false;

  linkModalVisible = false;
  drawerVisible = false;
  drawerLoading = false;
  drawerReason: any = null;
  currentReasonItem: any = null;
  currentReasonLink = '';
  currentReasonName = '';
  currentReasonDescription = '';
  currentReasonType: string | null = null;
  commonWebchatUrl = '';
  serviceNumberOptions: Array<{ id: string; name: string; description?: string; type?: string }> = [];
  serviceNumberLoading = false;
  serviceNumberLoadingMore = false;
  serviceNumberSearchKeyword = '';
  serviceNumberPageIndex = 0;
  readonly serviceNumberPageSize = 20;
  serviceNumberHasNextPage = false;
  selectedQrSize = 256;
  qrCodeDataUrl = '';
  qrGenerating = false;
  reasonLoginMode: WebchatLoginMode = 'anonymous';
  private readonly previewQrSize = 180;
  deleteConfirmDesc = '';
  deleteConfirmTitle = '';
  deleteConfirmItem1 = '';
  deleteConfirmItem2 = '';
  deleteConfirmItem3 = '';
  private serviceNumberDropdownScrollElement: HTMLElement | null = null;
  private serviceNumberDropdownElement: HTMLElement | null = null;
  private readonly serviceNumberDropdownScrollHandler = (): void => {
    this.checkServiceNumberDropdownNearBottom();
  };
  private readonly serviceNumberDropdownWheelHandler = (): void => {
    setTimeout(() => this.checkServiceNumberDropdownNearBottom(), 16);
  };
  private mobileLoadObserver?: IntersectionObserver;

  typeOptions = [
    { labelI18nKey: 'servicenumber.inbound-reason.type.scan-qr-code', value: 'SCAN_QR_CODE' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.product-consult', value: 'PRODUCT_CONSULT' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.promotion-consult', value: 'PROMOTION_CONSULT' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.signature', value: 'SIGNATURE' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.customer-support', value: 'CUSTOMER_SUPPORT' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.pricing-consult', value: 'PRICING_CONSULT' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.custom', value: 'CUSTOM' }
  ];
  statusOptions = [
    { labelI18nKey: 'servicenumber.inbound-reason.status.enable', value: 'Enable' },
    { labelI18nKey: 'servicenumber.inbound-reason.status.disable', value: 'Disable' },
    { labelI18nKey: 'servicenumber.inbound-reason.validity.expired', value: 'Expired' },
    { labelI18nKey: 'servicenumber.inbound-reason.validity.expiring', value: 'Expiring' }
  ];

  ngOnInit(): void {
    this.tenantId = this.tenantIdInput || this.route.snapshot.paramMap.get('id') || '';
    this.serviceNumberId =
      this.serviceNumberIdInput ||
      this.route.snapshot.queryParamMap.get('serviceNumberId') ||
      this.commonService.getCurrentTenantBossSnid() ||
      '';
    if (!this.isPersonTenant()) {
      if (!this.serviceNumberIdInput && !this.route.snapshot.queryParamMap.get('serviceNumberId')) {
        this.serviceNumberId = '';
        this.loadServiceNumberOptions();
      }
    }
    this.updateViewportMode();
    const hasRefreshFlag = this.route.snapshot.queryParamMap.has('refresh');
    this.refreshTime = Date.now();
    this.loadData({ includeRefreshTime: !hasRefreshFlag });
  }

  ngAfterViewInit(): void {
    this.syncMobileLoadObserver();
  }

  ngOnDestroy(): void {
    this.mobileLoadObserver?.disconnect();
    this.detachServiceNumberDropdownScrollListener();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportMode(true);
  }

  isPersonTenant(): boolean {
    return this.commonService.getCurrentTenant()?.type === 'Person';
  }

  normalizeStatus(status: any): string {
    if (typeof status === 'boolean') return status ? 'Enable' : 'Disable';
    const value = status === null || status === undefined ? '' : String(status);
    if (!value) return '';
    const upper = value.toUpperCase();
    if (upper === 'ENABLE' || upper === 'ENABLED') return 'Enable';
    if (upper === 'DISABLE' || upper === 'DISABLED') return 'Disable';
    return value;
  }

  getStatusI18nKey(item: any): string {
    const normalizedStatus = this.normalizeStatus(item?.status);
    const validityStatus = this.getValidityStatus(item);
    if (normalizedStatus === 'Disable') {
      return 'servicenumber.inbound-reason.status.disable';
    }
    if (validityStatus === 'EXPIRED') {
      return 'servicenumber.inbound-reason.validity.expired';
    }
    if (validityStatus === 'EXPIRING') {
      return 'servicenumber.inbound-reason.validity.expiring';
    }
    return 'servicenumber.inbound-reason.status.enable';
  }

  getStatusPillClass(item: any): string {
    const normalizedStatus = this.normalizeStatus(item?.status);
    if (normalizedStatus === 'Disable') {
      return 'status-pill-disable';
    }
    const validityStatus = this.getValidityStatus(item);
    if (validityStatus === 'EXPIRED') {
      return 'status-pill-expired';
    }
    if (validityStatus === 'EXPIRING') {
      return 'status-pill-expiring';
    }
    return 'status-pill-enable';
  }

  getInboundCountText(item: any): string {
    const raw = item?.inboundCount ?? item?.inboundTotal ?? item?.inboundNum;
    const num = typeof raw === 'number' ? raw : raw ? Number(raw) : NaN;
    if (!Number.isNaN(num)) {
      return new Intl.NumberFormat().format(num);
    }
    return '-';
  }

  getValidityLabel(item: any): string {
    const status = this.normalizeStatus(item?.status);
    if (status === 'Disable') {
      return this.i18n.fanyi('servicenumber.inbound-reason.validity.disabled');
    }
    const validity = this.getValidityTimestamp(item);
    if (!validity) {
      return this.i18n.fanyi('servicenumber.inbound-reason.validity.permanent');
    }
    return this.formatDate(validity);
  }

  getValidityColor(item: any): string {
    const status = this.normalizeStatus(item?.status);
    if (status === 'Disable') {
      return '#86909c';
    }
    const validityStatus = this.getValidityStatus(item);
    if (validityStatus === 'EXPIRED') {
      return '#FF4D4F';
    }
    if (validityStatus === 'EXPIRING') {
      return '#FA8C16';
    }
    if (validityStatus === 'PERMANENT') {
      return '#86909c';
    }
    const validity = this.getValidityTimestamp(item);
    if (!validity) {
      return '#86909c';
    }
    const now = Date.now();
    if (validity <= now) {
      return '#FF4D4F';
    }
    const threshold = now + 7 * 24 * 60 * 60 * 1000;
    if (validity <= threshold) {
      return '#FA8C16';
    }
    return '#86909c';
  }

  isExpired(item: any): boolean {
    return this.getValidityStatus(item) === 'EXPIRED';
  }

  getIconForType(type: string): string {
    const icons: { [key: string]: string } = {
      SCAN_QR_CODE: '📱',
      PRODUCT_CONSULT: '📦',
      PROMOTION_CONSULT: '🎉',
      SIGNATURE: '✍️',
      CUSTOMER_SUPPORT: '💬',
      PRICING_CONSULT: '💰',
      CUSTOM: '⚙️'
    };
    return icons[type] || '❓';
  }

  getRegexTooltip(item: any): string {
    if (!item?.regexRules || item.regexRules.length === 0) {
      return '';
    }
    const maxShow = 3;
    const rules = item.regexRules.slice(0, maxShow).join('\n');
    if (item.regexRules.length > maxShow) {
      return `${rules}\n...(+${item.regexRules.length - maxShow})`;
    }
    return rules;
  }

  createReason(): void {
    this.navigateToEditor();
  }

  onFilterChange(key: string, value: string | null): void {
    if (key === 'type') {
      this.typeFilter = value;
    } else if (key === 'status') {
      this.statusFilter = value;
    }
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  search(): void {
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  resetSearch(): void {
    this.searchKeywords = '';
    this.refreshTime = Date.now();
    this.pageIndex = 1;
    this.loadData();
  }

  get trimmedSearchKeywords(): string {
    return this.searchKeywords.trim();
  }

  hasActiveFilters(): boolean {
    return !!(this.trimmedSearchKeywords || this.typeFilter || this.statusFilter);
  }

  clearAllFilters(): void {
    this.searchKeywords = '';
    this.typeFilter = null;
    this.statusFilter = null;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  toggleMobileSearch(): void {
    if (!this.isMobileView) {
      return;
    }

    this.mobileSearchExpanded = !this.mobileSearchExpanded;
    if (this.mobileSearchExpanded) {
      setTimeout(() => this.searchInputElement?.nativeElement.focus(), 0);
    }
  }

  nzPageIndexChange(index: number): void {
    this.pageIndex = index;
    this.loadData();
  }

  nzPageSizeChange(size: number): void {
    this.pageSize = size;
    this.refreshTime = Date.now();
    this.pageIndex = 1;
    this.loadData();
  }

  private updateViewportMode(shouldReload = false): void {
    const nextMobile = typeof window !== 'undefined' && window.innerWidth <= 767;
    const modeChanged = this.isMobileView !== nextMobile;
    this.isMobileView = nextMobile;
    this.mobileSearchExpanded = !nextMobile;

    if (modeChanged && shouldReload) {
      this.pageIndex = 1;
      this.refreshTime = Date.now();
      this.loadData();
    } else {
      this.syncMobileLoadObserver();
    }
  }

  private updateMobileLoadState(): void {
    this.hasMoreMobile = this.isMobileView && this.listOfData.length < this.total;
    this.syncMobileLoadObserver();
  }

  private syncMobileLoadObserver(): void {
    this.mobileLoadObserver?.disconnect();

    if (!this.isMobileView || !this.hasMoreMobile || !this.mobileLoadMoreAnchor?.nativeElement) {
      return;
    }

    this.mobileLoadObserver = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.ngZone.run(() => this.loadMoreOnMobile());
        }
      },
      {
        root: this.pageScroll?.nativeElement ?? null,
        rootMargin: '0px 0px 220px 0px',
        threshold: 0.01
      }
    );

    this.mobileLoadObserver.observe(this.mobileLoadMoreAnchor.nativeElement);
  }

  loadMoreOnMobile(): void {
    if (!this.isMobileView || this.loading || this.isLoadingMore || !this.hasMoreMobile) {
      return;
    }

    this.loadData({
      append: true,
      pageIndex: this.pageIndex + 1
    });
  }

  loadData(options: { append?: boolean; pageIndex?: number; includeRefreshTime?: boolean } = {}): void {
    const append = !!options.append;
    const requestPageIndex = options.pageIndex ?? this.pageIndex;
    const includeRefreshTime = options.includeRefreshTime ?? true;

    if (!this.serviceNumberId) {
      this.listOfData = [];
      this.total = 0;
      this.loading = false;
      this.isLoadingMore = false;
      return;
    }

    if (append) {
      this.isLoadingMore = true;
    } else {
      this.loading = true;
    }
    const normalizedStatus = this.statusFilter ? this.normalizeStatus(this.statusFilter) : undefined;
    const payload: any = {
      serviceNumberId: this.serviceNumberId,
      pageIndex: requestPageIndex - 1,
      pageSize: this.pageSize,
      direction: this.direction,
      key: this.searchKeywords || undefined,
      sourceType: this.typeFilter || undefined,
      status: normalizedStatus || undefined
    };
    if (includeRefreshTime && this.refreshTime) {
      payload.refreshTime = this.refreshTime;
    }
    this.http
      .post(ConstantUtil.TENANT_INBOUND_REASON_V1_LIST, payload)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.isLoadingMore = false;
          this.cdr.detectChanges();
          this.syncMobileLoadObserver();
        })
      )
      .subscribe((res: any) => {
        if (res?.success) {
          this.commonWebchatUrl = res?.data?.webchatUrl ?? '';
          const items = res?.data?.items || [];
          const mappedItems = items.map((item: any) => this.mapReasonItem(item));
          this.pageIndex = requestPageIndex;
          this.listOfData = append ? [...this.listOfData, ...mappedItems] : mappedItems;
          this.total = res?.data?.totalCount || 0;
        } else {
          if (!append) {
            this.listOfData = [];
            this.total = 0;
          }
        }
        this.updateMobileLoadState();
      });
  }

  viewReason(item: any): void {
    if (!item) {
      return;
    }
    this.currentReasonItem = item;
    this.currentReasonName = item?.title || '';
    this.currentReasonDescription = item?.description || '';
    this.currentReasonType = item?.type || item?.sourceType || null;
    this.selectedQrSize = 256;
    this.reasonLoginMode = 'anonymous';
    this.currentReasonLink = this.canGenerateReasonLink() ? this.buildReasonLink(item) : '';
    this.linkModalVisible = true;
    this.updateQrCode();
    this.cdr.detectChanges();
  }

  onLinkServiceNumberChange(): void {
    if (!this.currentReasonItem) {
      return;
    }
    this.currentReasonLink = this.canGenerateReasonLink() ? this.buildReasonLink(this.currentReasonItem) : '';
    this.updateQrCode();
    this.cdr.detectChanges();
  }

  onReasonLoginModeChange(mode: WebchatLoginMode): void {
    this.reasonLoginMode = mode || 'anonymous';
    this.currentReasonLink = this.currentReasonItem && this.canGenerateReasonLink() ? this.buildReasonLink(this.currentReasonItem) : '';
    this.updateQrCode();
    this.cdr.detectChanges();
  }

  onServiceNumberSearch(keyword: string): void {
    this.serviceNumberSearchKeyword = keyword.trim();
    this.serviceNumberPageIndex = 0;
    this.loadServiceNumberOptions();
  }

  onServiceNumberScrollToBottom(): void {
    if (this.serviceNumberLoading || !this.serviceNumberHasNextPage) {
      return;
    }
    this.serviceNumberPageIndex += 1;
    this.fetchServiceNumberOptions(true);
  }

  onServiceNumberDropdownOpenChange(open: boolean): void {
    if (!open) {
      this.detachServiceNumberDropdownScrollListener();
      return;
    }

    this.scheduleServiceNumberDropdownScrollBinding();
  }

  generateLinkFromDrawer(): void {
    if (!this.drawerReason) {
      return;
    }
    this.viewReason(this.drawerReason);
  }

  openDetail(item: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!item) {
      return;
    }
    this.drawerVisible = true;
    this.drawerReason = item;
    if (item?.id) {
      this.loadDetail(item.id);
    } else {
      this.cdr.detectChanges();
    }
  }

  closeDrawer(): void {
    this.drawerVisible = false;
    this.drawerReason = null;
    this.drawerLoading = false;
  }

  closeLinkModal(): void {
    this.linkModalVisible = false;
    this.currentReasonItem = null;
    this.currentReasonLink = '';
    this.currentReasonName = '';
    if (!this.isPersonTenant() && !this.embedded) {
      this.serviceNumberId = '';
    }
    this.reasonLoginMode = 'anonymous';
    this.qrCodeDataUrl = '';
    this.qrGenerating = false;
  }

  copyCurrentLink(): void {
    const text = this.currentReasonLink || '';
    if (!text) {
      return;
    }
    const nav: any = navigator as any;
    if (nav.clipboard && nav.clipboard.writeText) {
      nav.clipboard
        .writeText(text)
        .then(() => {
          this.message.success(this.i18n.fanyi('common.copy-success'));
        })
        .catch(() => {
          this.copyLinkByTextarea(text);
        });
      return;
    }
    this.copyLinkByTextarea(text);
  }

  async copyRegexRule(rule: string, event?: Event): Promise<void> {
    if (event) {
      event.stopPropagation();
    }
    const text = (rule || '').trim();
    if (!text) {
      return;
    }
    const nav: any = navigator as any;
    if (nav.clipboard && nav.clipboard.writeText) {
      try {
        await nav.clipboard.writeText(text);
        this.message.success(this.i18n.fanyi('common.copy-success'));
        return;
      } catch {
        this.copyLinkByTextarea(text);
        return;
      }
    }
    this.copyLinkByTextarea(text);
  }

  onQrSizeChange(size: number): void {
    if (!size || size === this.selectedQrSize) {
      return;
    }
    this.selectedQrSize = size;
    this.updateQrCode();
  }

  downloadQr(format: 'png' | 'svg'): void {
    if (!this.currentReasonLink) {
      return;
    }
    const size = this.selectedQrSize || 256;
    const link = this.currentReasonLink;
    if (format === 'png') {
      QRCode.toDataURL(link, { width: size, margin: 1 })
        .then((url: string) => {
          const a = document.createElement('a');
          a.href = url;
          a.download = this.buildQrFileName('png');
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        })
        .catch(() => {
          this.message.error(this.i18n.fanyi('servicenumber.inbound-reason.qr.error.png-download'));
        });
    } else {
      QRCode.toString(link, { type: 'svg', width: size, margin: 1 })
        .then((svg: string) => {
          const blob = new Blob([svg], { type: 'image/svg+xml' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = this.buildQrFileName('svg');
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.message.error(this.i18n.fanyi('servicenumber.inbound-reason.qr.error.svg-download'));
        });
    }
  }

  private updateQrCode(): void {
    if (!this.currentReasonLink) {
      this.qrCodeDataUrl = '';
      return;
    }
    const size = this.previewQrSize;
    this.qrGenerating = true;
    QRCode.toDataURL(this.currentReasonLink, { width: size, margin: 1 })
      .then((url: string) => {
        this.qrCodeDataUrl = url;
        this.qrGenerating = false;
        this.cdr.detectChanges();
      })
      .catch(() => {
        this.qrGenerating = false;
        this.qrCodeDataUrl = '';
        this.message.error(this.i18n.fanyi('servicenumber.inbound-reason.qr.error.generate'));
        this.cdr.detectChanges();
      });
  }

  private buildQrFileName(ext: 'png' | 'svg'): string {
    const name = this.currentReasonName || 'webchat';
    const size = this.selectedQrSize || 256;
    const safeName = name.replace(/[^a-zA-Z0-9_-]+/g, '_');
    return `${safeName || 'webchat'}_${size}.${ext}`;
  }

  private copyLinkByTextarea(text: string): void {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      this.message.success(this.i18n.fanyi('common.copy-success'));
    } catch {
      this.message.error(this.i18n.fanyi('common.copy-failed'));
    } finally {
      document.body.removeChild(ta);
    }
  }

  private buildReasonLink(item: any): string {
    const baseUrl = this.commonWebchatUrl || item?.webchatUrl || item?.webchatURL || item?.webChatUrl;
    if (!baseUrl) {
      return '';
    }
    const isAbsolute = /^https?:\/\//i.test(baseUrl);
    try {
      const parsed = new URL(baseUrl, window.location.origin);
      const params = parsed.searchParams;
      const sourceId = String(item?.id || item?.sourceId || '').trim();
      const serviceNumberId = String(this.serviceNumberId || '').trim();
      const tenantCode = String(this.commonService.getCurrentTenant()?.code || '').trim();

      if (sourceId) {
        params.set('sourceId', sourceId);
      }
      if (serviceNumberId && !params.has('id')) {
        params.set('id', serviceNumberId);
      }
      if (tenantCode && !params.has('tenantCode')) {
        params.set('tenantCode', tenantCode);
      }
      if (!params.has('action')) {
        params.set('action', 'service');
      }
      if (!params.has('customData')) {
        params.set('customData', 'default');
      }
      params.set('loginMode', this.reasonLoginMode || 'anonymous');

      if (isAbsolute) {
        return parsed.toString();
      }
      const result = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      if (baseUrl.startsWith('/')) {
        return result;
      }
      return result.startsWith('/') ? result.slice(1) : result;
    } catch {
      const query = new URLSearchParams();
      const sourceId = String(item?.id || item?.sourceId || '').trim();
      const serviceNumberId = String(this.serviceNumberId || '').trim();
      const tenantCode = String(this.commonService.getCurrentTenant()?.code || '').trim();
      const [path, originalQuery = ''] = baseUrl.split('?');
      const current = new URLSearchParams(originalQuery);
      current.forEach((value, key) => query.set(key, value));
      if (sourceId) query.set('sourceId', sourceId);
      if (serviceNumberId && !query.has('id')) query.set('id', serviceNumberId);
      if (tenantCode && !query.has('tenantCode')) query.set('tenantCode', tenantCode);
      if (!query.has('action')) query.set('action', 'service');
      if (!query.has('customData')) query.set('customData', 'default');
      query.set('loginMode', this.reasonLoginMode || 'anonymous');
      const suffix = query.toString();
      if (!suffix) return baseUrl;
      return `${path}?${suffix}`;
    }
  }

  private canGenerateReasonLink(): boolean {
    return !!String(this.serviceNumberId || '').trim();
  }

  private loadServiceNumberOptions(): void {
    if (!this.tenantId) {
      return;
    }
    this.serviceNumberPageIndex = 0;
    this.fetchServiceNumberOptions(false);
  }

  private fetchServiceNumberOptions(append: boolean): void {
    this.serviceNumberLoading = true;
    this.serviceNumberLoadingMore = append;
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_V2_LIST, {
        tenantId: this.tenantId,
        pageIndex: this.serviceNumberPageIndex,
        pageSize: this.serviceNumberPageSize,
        direction: 'desc',
        status: 'Enable',
        openType: 'External',
        key: this.serviceNumberSearchKeyword || undefined
      })
      .pipe(
        finalize(() => {
          this.serviceNumberLoading = false;
          this.serviceNumberLoadingMore = false;
          this.cdr.detectChanges();
          this.scheduleServiceNumberDropdownScrollBinding();
        })
      )
      .subscribe((res: any) => {
        if (res?.success) {
          const items = res?.data?.items || [];
          const mappedOptions = items.map((item: any) => ({
            id: item.id,
            name: item.name,
            description: item.description,
            type: item.type
          }));
          this.serviceNumberHasNextPage = !!res?.data?.hasNextPage;
          this.serviceNumberOptions = this.mergeServiceNumberOptions(mappedOptions, append);
        } else {
          this.serviceNumberHasNextPage = false;
          if (!append) {
            this.serviceNumberOptions = [];
          }
        }
      });
  }

  private mergeServiceNumberOptions(
    incoming: Array<{ id: string; name: string; description?: string; type?: string }>,
    append: boolean
  ): Array<{ id: string; name: string; description?: string; type?: string }> {
    const existing = append ? this.serviceNumberOptions : [];
    const selected = this.serviceNumberOptions.find(item => item.id === this.serviceNumberId);
    const merged = [...existing, ...incoming];
    if (selected && !merged.some(item => item.id === selected.id)) {
      merged.unshift(selected);
    }
    const seen = new Set<string>();
    return merged.filter(item => {
      if (!item?.id || seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
  }

  private scheduleServiceNumberDropdownScrollBinding(): void {
    setTimeout(() => {
      const dropdownElement = this.getVisibleServiceNumberDropdownElement();
      const scrollElement = this.getVisibleServiceNumberDropdownScrollElement();

      if (!dropdownElement || !scrollElement) {
        return;
      }

      if (this.serviceNumberDropdownElement !== dropdownElement) {
        if (this.serviceNumberDropdownElement) {
          this.serviceNumberDropdownElement.removeEventListener('wheel', this.serviceNumberDropdownWheelHandler);
        }
        this.serviceNumberDropdownElement = dropdownElement;
        this.serviceNumberDropdownElement.addEventListener('wheel', this.serviceNumberDropdownWheelHandler, { passive: true });
      }

      if (this.serviceNumberDropdownScrollElement !== scrollElement) {
        if (this.serviceNumberDropdownScrollElement) {
          this.serviceNumberDropdownScrollElement.removeEventListener('scroll', this.serviceNumberDropdownScrollHandler);
        }
        this.serviceNumberDropdownScrollElement = scrollElement;
        this.serviceNumberDropdownScrollElement.addEventListener('scroll', this.serviceNumberDropdownScrollHandler, { passive: true });
      }

      this.checkServiceNumberDropdownNearBottom();
    }, 32);
  }

  private detachServiceNumberDropdownScrollListener(): void {
    if (!this.serviceNumberDropdownScrollElement) {
      if (this.serviceNumberDropdownElement) {
        this.serviceNumberDropdownElement.removeEventListener('wheel', this.serviceNumberDropdownWheelHandler);
        this.serviceNumberDropdownElement = null;
      }
      return;
    }

    this.serviceNumberDropdownScrollElement.removeEventListener('scroll', this.serviceNumberDropdownScrollHandler);
    this.serviceNumberDropdownScrollElement = null;
    if (this.serviceNumberDropdownElement) {
      this.serviceNumberDropdownElement.removeEventListener('wheel', this.serviceNumberDropdownWheelHandler);
      this.serviceNumberDropdownElement = null;
    }
  }

  private checkServiceNumberDropdownNearBottom(): void {
    const el = this.serviceNumberDropdownScrollElement;
    if (!el || this.serviceNumberLoading || !this.serviceNumberHasNextPage) {
      return;
    }

    const hasOverflow = el.scrollHeight > el.clientHeight + 1;
    if (!hasOverflow) {
      this.onServiceNumberScrollToBottom();
      return;
    }

    const threshold = 56;
    const reachedThreshold = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (reachedThreshold) {
      this.onServiceNumberScrollToBottom();
    }
  }

  private getVisibleServiceNumberDropdownElement(): HTMLElement | null {
    const candidates = Array.from(document.querySelectorAll<HTMLElement>('.inbound-reason-service-number-dropdown'));
    return candidates.find(element => element.getClientRects().length > 0) ?? candidates.at(-1) ?? null;
  }

  private getVisibleServiceNumberDropdownScrollElement(): HTMLElement | null {
    const candidates = Array.from(
      document.querySelectorAll<HTMLElement>(
        '.inbound-reason-service-number-dropdown .rc-virtual-list-holder, .inbound-reason-service-number-dropdown .cdk-virtual-scrollable'
      )
    );
    return candidates.find(element => element.getClientRects().length > 0) ?? candidates.at(-1) ?? null;
  }

  private mapReasonItem(item: any): any {
    const status = this.normalizeStatus(item?.status ?? item?.statusKey);
    const inboundCount = item?.inboundCount;
    const inboundTransferCount = item?.inboundTransferCount;
    const validityPeriod = item?.validityPeriod ?? item?.expireTime ?? item?.expireAt;
    const webchatUrl = this.commonWebchatUrl || item?.webchatUrl || item?.webchatURL || item?.webChatUrl;
    return {
      ...item,
      title: item?.sourceName ?? item?.title ?? '',
      type: item?.sourceType ?? item?.type ?? '',
      description: item?.description ?? '',
      status,
      inboundCount,
      inboundTransferCount,
      validityPeriod,
      webchatUrl
    };
  }

  private loadDetail(id: string): void {
    this.drawerLoading = true;
    this.http
      .post(`${ConstantUtil.TENANT_INBOUND_REASON_V1_ITEM}/${id}`, {})
      .pipe(
        finalize(() => {
          this.drawerLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((res: any) => {
        if (res?.success && res?.data) {
          const mapped = this.mapReasonItem(res.data);
          const previous = this.drawerReason;
          if (previous) {
            const inboundFallback = previous.inboundCount;
            const humanFallback = previous.inboundTransferCount;
            if (mapped.inboundCount === null || mapped.inboundCount === undefined || mapped.inboundCount === '') {
              mapped.inboundCount = inboundFallback ?? mapped.inboundCount;
            }
            if (mapped.inboundTransferCount === null || mapped.inboundTransferCount === undefined || mapped.inboundTransferCount === '') {
              mapped.inboundTransferCount = humanFallback ?? mapped.inboundTransferCount;
            }
            this.drawerReason = { ...previous, ...mapped };
          } else {
            this.drawerReason = mapped;
          }
        }
      });
  }

  private getNumberValue(item: any, keys: string[]): number | null {
    for (const key of keys) {
      const raw = item?.[key];
      if (raw === 0) {
        return 0;
      }
      if (raw !== null && raw !== undefined && raw !== '') {
        const num = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isNaN(num)) {
          return num;
        }
      }
    }
    return null;
  }

  private getValidityStatus(item: any): string {
    const raw = item?.validityStatus ?? item?.expireStatus;
    if (raw) {
      return String(raw).toUpperCase();
    }
    const validity = this.getValidityTimestamp(item);
    if (!validity) {
      return 'PERMANENT';
    }
    const now = Date.now();
    if (validity <= now) {
      return 'EXPIRED';
    }
    const threshold = now + 7 * 24 * 60 * 60 * 1000;
    if (validity <= threshold) {
      return 'EXPIRING';
    }
    return '';
  }

  private getCurrentTenantFromCache(): any | null {
    const tenantStr = localStorage.getItem('current_tenant');
    if (!tenantStr) {
      return null;
    }
    try {
      const t = JSON.parse(tenantStr);
      return t;
    } catch {
      return null;
    }
  }

  editReason(item: any): void {
    if (!item?.id) {
      return;
    }
    this.navigateToEditor(item.id);
  }

  /**
   * 導向進線原因編輯頁，並保留服務號詳情返回位置。
   */
  private navigateToEditor(reasonId?: string): void {
    const params = new URLSearchParams();
    if (reasonId) params.set('reasonId', reasonId);
    if (this.serviceNumberId) params.set('serviceNumberId', this.serviceNumberId);
    if (this.embedded) params.set('returnUrl', this.getEmbeddedReturnUrl());
    const query = params.toString();
    this.router.navigateByUrl(`/personal-inbound-reason/${this.tenantId}/create${query ? `?${query}` : ''}`);
  }

  /**
   * Keep the detail page on the inbound-reason tab after returning from the editor.
   * The detail component updates the address bar directly, so Router.url can miss
   * the currently selected tab; explicitly restoring it here avoids falling back
   * to the basic information tab.
   */
  private getEmbeddedReturnUrl(): string {
    const [path, hash = ''] = this.router.url.split('#', 2);
    const [pathname, query = ''] = path.split('?', 2);
    const queryParams = new URLSearchParams(query);
    queryParams.set('tab', 'inbound-reason');
    const queryString = queryParams.toString();
    return `${pathname}${queryString ? `?${queryString}` : ''}${hash ? `#${hash}` : ''}`;
  }

  getTypeLabel(item: any): string {
    const type = item?.type || item?.sourceType;
    if (!type) {
      return '-';
    }
    const matched = this.typeOptions.find(opt => opt.value === type);
    return matched ? this.i18n.fanyi(matched.labelI18nKey) : String(type);
  }

  getCompactTypeOptionLabel(type: string | null): string {
    if (!type) {
      return '';
    }
    const fullLabel = this.getTypeLabel({ type });
    return fullLabel.split(' - ')[0]?.trim() || fullLabel;
  }

  getOverviewTotal(item: any): number | null {
    return this.getNumberValue(item, ['inboundCount']);
  }

  getOverviewHuman(item: any): number | null {
    return this.getNumberValue(item, ['inboundTransferCount']);
  }

  getOverviewConversion(item: any): number | null {
    const total = this.getOverviewTotal(item);
    const human = this.getOverviewHuman(item);
    if (total === null || total === 0 || human === null) {
      return null;
    }
    return human / total;
  }

  formatNumber(value: number | null): string {
    if (value === null || Number.isNaN(value)) {
      return '-';
    }
    return new Intl.NumberFormat().format(value);
  }

  formatRate(value: number | null): string {
    if (value === null || Number.isNaN(value)) {
      return '-';
    }
    let num = value;
    if (num >= 0 && num <= 1) {
      num = num * 100;
    }
    const fixed = Number.isInteger(num) ? String(num) : num.toFixed(1);
    return `${fixed}%`;
  }

  formatDateTime(value: any): string {
    if (!value) {
      return '-';
    }
    const timestamp = typeof value === 'number' ? value : new Date(value).getTime();
    if (!timestamp || Number.isNaN(timestamp)) {
      return '-';
    }
    return new Date(timestamp).toLocaleString();
  }

  getCreatedAt(item: any): any {
    if (!item) return null;
    return item.createTime ?? item.createdAt ?? item.createdTime ?? null;
  }

  getUpdatedAt(item: any): any {
    if (!item) return null;
    return item.updateTime ?? item.updatedAt ?? item.updatedTime ?? null;
  }

  deleteReason(item: any): void {
    if (!item?.id) {
      return;
    }
    const deleteTitle = this.i18n.fanyi('servicenumber.inbound-reason.delete.title');
    this.deleteConfirmTitle = deleteTitle;
    this.deleteConfirmDesc = this.i18n.fanyi('servicenumber.inbound-reason.delete.desc');
    this.deleteConfirmItem1 = this.i18n.fanyi('servicenumber.inbound-reason.delete.item1');
    this.deleteConfirmItem2 = this.i18n.fanyi('servicenumber.inbound-reason.delete.item2');
    this.deleteConfirmItem3 = this.i18n.fanyi('servicenumber.inbound-reason.delete.item3');
    this.modal.confirm({
      nzTitle: this.deleteConfirmTitleTpl,
      nzContent: this.deleteConfirmTpl,
      nzIconType: '',
      nzOkDanger: true,
      nzOkText: this.i18n.fanyi('common.delete'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOnOk: () => {
        this.loading = true;
        this.http
          .post(`${ConstantUtil.TENANT_INBOUND_REASON_V1_DELETE}/${item.id}`, {})
          .pipe(
            finalize(() => {
              this.loading = false;
              this.cdr.detectChanges();
            })
          )
          .subscribe((res: any) => {
            if (res?.success) {
              this.message.success(this.i18n.fanyi('delete-success'));
              this.refreshTime = Date.now();
              this.pageIndex = 1;
              this.loadData({ includeRefreshTime: false });
            } else {
              showFallbackHttpError(this.message, res?.msg, this.i18n.fanyi('delete-failed'));
            }
          });
      }
    });
  }

  toggleStatus(item: any): void {
    if (!item?.id) {
      return;
    }
    const normalized = this.normalizeStatus(item?.status);
    const shouldEnable = normalized !== 'Enable';
    const endpoint = shouldEnable ? ConstantUtil.TENANT_INBOUND_REASON_V1_ENABLE : ConstantUtil.TENANT_INBOUND_REASON_V1_DISABLE;
    this.loading = true;
    this.http
      .post(`${endpoint}/${item.id}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((res: any) => {
        if (res?.success) {
          const nextStatus = shouldEnable ? 'Enable' : 'Disable';
          const successKey = shouldEnable ? 'servicenumber.inbound-reason.enable-success' : 'servicenumber.inbound-reason.disable-success';
          item.status = nextStatus;
          this.message.success(this.i18n.fanyi(successKey));
          this.refreshTime = Date.now();
          this.loadData({ includeRefreshTime: false });
        } else {
          const failedKey = shouldEnable ? 'servicenumber.inbound-reason.enable-failed' : 'servicenumber.inbound-reason.disable-failed';
          showFallbackHttpError(this.message, res?.msg, this.i18n.fanyi(failedKey));
        }
      });
  }

  private getValidityTimestamp(item: any): number {
    const raw = item?.validityPeriod ?? item?.expireTime ?? item?.expireAt;
    if (!raw) return 0;
    if (typeof raw === 'number') return raw;
    const parsed = new Date(raw).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
